/**
 * Turning what someone typed into what gets stored (REPORT-SPEC §5.3).
 *
 * ## Why this lives in `site/` when the enforcement is server-side
 *
 * §15.2 requires client and server normalization fixtures to be **byte-
 * identical**. Two implementations kept in step by a shared fixture file are
 * two implementations; the fixture proves they agree on the cases someone
 * thought of, and says nothing about the first case nobody did. One function
 * with two callers cannot disagree at all.
 *
 * `site/` is the package everything else imports from — `og/` already reaches
 * into it, and `report/` reaches into both. So the contract lives at the root
 * of that graph, and the browser and the Lambda run the same code.
 *
 * The division of labour is unchanged by sharing it: the client's pass is a
 * courtesy to the person typing, and the server's is the one that decides what
 * is stored, because a request does not have to come from our dialog.
 *
 * ## The one rule that shapes all the others
 *
 * **Do not rewrite the visitor's words.** Everything here either fixes a
 * transport artefact (CRLF from a Windows browser), settles a representation
 * that has two spellings of the same text (NFC), or refuses a character that
 * exists to deceive a reader (bidi overrides). Nothing here improves prose,
 * collapses whitespace, or trims interior blank lines: someone reporting a bug
 * may well have laid a tensor name out over three lines, and that layout is
 * evidence.
 *
 * ## Why "character" means UTF-16 code unit
 *
 * §5.3 pins it, and the reason is that three counters must agree: the browser's
 * `maxlength`, the client's own `String.length`, and the Lambda's. Code units
 * are the one measure all three share for free. It is slightly conservative for
 * emoji — an astral character costs two — which is the right direction for a
 * bound whose failure mode is a visitor typing into a box that then refuses
 * their report.
 *
 * Counting grapheme clusters would be more humane and would need
 * `Intl.Segmenter` on both sides, agreeing across browser versions, to avoid
 * the exact client/server disagreement this measure is chosen to prevent.
 */

/**
 * Characters refused outright (§5.3).
 *
 * - C0 controls except LF and tab, and DEL. A report is plain text; a NUL or an
 *   ESC in it is either a mistake or an attempt at something.
 * - Bidirectional overrides and isolates (`U+202A`–`U+202E`, `U+2066`–`U+2069`).
 *   These reorder how text *renders* without changing what it contains, so a
 *   terminal, a code review and a database can show three different readings of
 *   one string. The ops CLI escapes them again on output (§10.2), because
 *   defending at one layer only is how a later output path becomes a hole.
 *
 * Written as escapes, never as the characters themselves. A literal control
 * byte in a source file makes `grep` treat the whole file as binary and report
 * **no matches, silently** — a lesson this repo has already paid for twice.
 *
 * CR (`U+000D`) is in the set. `normalizeDetails` converts it to LF *before*
 * testing, so a Windows browser's CRLF is accepted; anything reaching this test
 * with a bare CR left in it is a field where a line break has no business.
 */
// biome-ignore lint/suspicious/noControlCharactersInRegex: naming control characters is the entire purpose of this pattern. The rule guards against including one by accident; here they are the subject, written as escapes so the file stays greppable.
const FORBIDDEN = /[\u0000-\u0008\u000B-\u001F\u007F\u202A-\u202E\u2066-\u2069]/;

export type NormalizeResult =
  | { readonly ok: true; readonly value: string }
  | { readonly ok: false; readonly reason: "forbidden_character" | "too_long" };

/**
 * §5.3's five steps, in order, then the bound.
 *
 * Order matters in one place worth naming: the length check runs **after**
 * normalization, so a visitor is measured on what will be stored rather than on
 * what they pasted. NFC can shorten a string — `e` followed by a combining
 * acute is two code units and `é` is one — and rejecting a report for the
 * decomposed length of text that stores shorter would be a bound nobody could
 * predict from the counter in front of them.
 */
export function normalizeDetails(input: string, maxLength: number): NormalizeResult {
  // 1. CRLF and bare CR to LF. First, because a bare CR is a control character
  //    and would trip the check below if it survived to it.
  const unixNewlines = input.replace(/\r\n?/g, "\n");

  // 2. NFC. Two byte sequences that render identically become one, so a digest
  //    over the result is stable and a search for a tensor name matches it.
  const composed = unixNewlines.normalize("NFC");

  // 3. Trim leading and trailing Unicode whitespace. `String.prototype.trim` is
  //    Unicode-aware and includes line terminators, so a report that is nothing
  //    but blank lines becomes empty rather than nearly empty.
  const trimmed = composed.trim();

  // 4/5. Refuse the forbidden set; keep everything else exactly as written.
  if (FORBIDDEN.test(trimmed)) return { ok: false, reason: "forbidden_character" };
  if (trimmed.length > maxLength) return { ok: false, reason: "too_long" };

  return { ok: true, value: trimmed };
}

/**
 * The same refusal applied to a short single-line field — `part`, `partKey`,
 * `tower`, `selectedTensor`, `siteBuild`.
 *
 * Not `normalizeDetails`, because these are identifiers copied out of a model
 * rather than prose. A newline in a tensor name is not a formatting choice; it
 * is a sign the value did not come from where it claims to.
 *
 * They are still NFC-normalized and trimmed: a trailing space on `part` would
 * hash to a different card key than the same label without one, which is the
 * component-identity failure §6.0 exists to close.
 */
export function normalizeIdentifier(input: string, maxLength: number): NormalizeResult {
  const composed = input.normalize("NFC").trim();
  if (composed.includes("\n") || composed.includes("\t")) {
    return { ok: false, reason: "forbidden_character" };
  }
  if (FORBIDDEN.test(composed)) return { ok: false, reason: "forbidden_character" };
  if (composed.length > maxLength) return { ok: false, reason: "too_long" };
  return { ok: true, value: composed };
}
